import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LocaleContext';
import { useServiceCatalog } from '../hooks/useServiceCatalog';
import { supabase } from '../lib/supabase';
import { displayName, formatWhen } from '../lib/format';

const DIMENSIONS = [
  { key: 'rating_service_advisor', labelKey: 'reviews.advisor' },
  { key: 'rating_mechanic', labelKey: 'reviews.mechanic' },
  { key: 'rating_speed', labelKey: 'reviews.speed' },
  { key: 'rating_price_transparency', labelKey: 'reviews.price' },
  { key: 'rating_cleanliness', labelKey: 'reviews.cleanliness' },
  { key: 'rating_pickup_delivery', labelKey: 'reviews.pickup' },
  { key: 'rating_overall', labelKey: 'reviews.overall' },
];

function Stars({ value }) {
  const n = Number(value) || 0;
  return (
    <span className="review-stars" aria-label={`${n} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={`material-symbol review-star ${i <= n ? 'filled' : ''}`}
          aria-hidden="true"
        >
          star
        </span>
      ))}
    </span>
  );
}

export default function ReviewsPage() {
  const t = useT();
  const { isManager } = useAuth();
  const { serviceLabel } = useServiceCatalog();
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({ low_rating_threshold: 3, alerts_enabled: true });
  const [threshold, setThreshold] = useState(3);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [filterLow, setFilterLow] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    async function load() {
      const [reviewRes, settingsRes] = await Promise.all([
        supabase
          .from('service_reviews')
          .select(
            '*, bookings:booking_id(service_type, scheduled_at), profiles:customer_id(first_name, last_name, full_name), vehicles:vehicle_id(brand, model, plate_number)'
          )
          .order('created_at', { ascending: false })
          .limit(120),
        supabase.from('review_alert_settings').select('*').eq('id', true).maybeSingle(),
      ]);
      if (!alive) return;
      if (reviewRes.error) {
        setError(reviewRes.error.message);
        return;
      }
      setReviews(reviewRes.data || []);
      if (settingsRes.data) {
        setSettings(settingsRes.data);
        setThreshold(settingsRes.data.low_rating_threshold ?? 3);
        setAlertsEnabled(settingsRes.data.alerts_enabled !== false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!filterLow) return reviews;
    return reviews.filter(
      (row) => Number(row.lowest_rating) <= Number(settings.low_rating_threshold || 3)
    );
  }, [reviews, filterLow, settings.low_rating_threshold]);

  const stats = useMemo(() => {
    if (!reviews.length) {
      return { count: 0, avgOverall: 0, lowCount: 0 };
    }
    const sum = reviews.reduce((acc, row) => acc + Number(row.rating_overall || 0), 0);
    const lowCount = reviews.filter(
      (row) => Number(row.lowest_rating) <= Number(settings.low_rating_threshold || 3)
    ).length;
    return {
      count: reviews.length,
      avgOverall: (sum / reviews.length).toFixed(1),
      lowCount,
    };
  }, [reviews, settings.low_rating_threshold]);

  async function saveSettings() {
    setSaving(true);
    setSaved(false);
    setError('');
    const { error: rpcError } = await supabase.rpc('update_review_alert_settings', {
      p_threshold: threshold,
      p_enabled: alertsEnabled,
    });
    setSaving(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    setSettings({ low_rating_threshold: threshold, alerts_enabled: alertsEnabled });
    setSaved(true);
  }

  return (
    <section className="reviews-page">
      <header className="page-head">
        <div>
          <span className="kicker">{t('reviews.kicker')}</span>
          <h1>{t('reviews.title')}</h1>
          <p className="lede">
            {t('reviews.lede')}
          </p>
        </div>
      </header>

      <div className="stat-grid compact review-stats">
        <article className="stat-card">
          <span>{t('reviews.count')}</span>
          <strong>{stats.count}</strong>
        </article>
        <article className="stat-card">
          <span>{t('reviews.avg')}</span>
          <strong>{stats.avgOverall || '—'}</strong>
        </article>
        <article className="stat-card review-stat-warn">
          <span>{t('reviews.below')}</span>
          <strong>{stats.lowCount}</strong>
        </article>
      </div>

      {isManager ? (
        <div className="review-alert-panel">
          <div className="review-alert-head">
            <h2>{t('reviews.alerts')}</h2>
            <p>
              {t('reviews.notify')}
            </p>
          </div>
          <div className="review-alert-controls">
            <label className="review-threshold">
              <span>{t('reviews.threshold')}</span>
              <div className="review-threshold-row">
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                />
                <strong>{threshold} / 5</strong>
              </div>
            </label>
            <label className="review-toggle">
              <input
                type="checkbox"
                checked={alertsEnabled}
                onChange={(e) => setAlertsEnabled(e.target.checked)}
              />
              <span>{t('reviews.sendAlerts')}</span>
            </label>
            <button type="button" className="primary review-save-btn" disabled={saving} onClick={saveSettings}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
          {saved ? <p className="ok review-saved">Alert settings saved.</p> : null}
        </div>
      ) : null}

      <div className="review-toolbar">
        <button
          type="button"
          className={filterLow ? 'pill active' : 'pill'}
          onClick={() => setFilterLow((v) => !v)}
        >
          {filterLow ? 'Showing low scores' : 'Show low scores only'}
        </button>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <div className="review-list">
        {filtered.length === 0 ? (
          <p className="muted">No verified reviews yet.</p>
        ) : (
          filtered.map((row) => {
            const customer = displayName(row.profiles);
            const vehicle = row.vehicles;
            const carLabel = [vehicle?.brand, vehicle?.model, vehicle?.plate_number]
              .filter(Boolean)
              .join(' · ');
            const isLow =
              Number(row.lowest_rating) <= Number(settings.low_rating_threshold || 3);
            return (
              <article
                key={row.id}
                className={`review-card ${isLow ? 'review-card-low' : ''}`}
              >
                <div className="review-card-head">
                  <div className="review-card-main">
                    <div className="review-badges">
                      <span className="pill status-sent">{t('reviews.verified')}</span>
                      {isLow ? <span className="pill status-warn">{t('reviews.lowScore')}</span> : null}
                    </div>
                    <h3>{serviceLabel(row.bookings?.service_type, 'common.visit')}</h3>
                    <p className="muted review-meta">
                      {customer}
                      {carLabel ? ` · ${carLabel}` : ''}
                      {' · '}
                      {formatWhen(row.created_at)}
                    </p>
                  </div>
                  <div className="review-overall">
                    <Stars value={row.rating_overall} />
                    <small>{t('reviews.overall')}</small>
                  </div>
                </div>
                <div className="review-dimensions">
                  {DIMENSIONS.filter((dim) => dim.key !== 'rating_overall').map((dim) => {
                    const value = row[dim.key];
                    if (value == null && dim.key === 'rating_pickup_delivery') return null;
                    return (
                      <div key={dim.key} className="review-dimension">
                        <span>{t(dim.labelKey)}</span>
                        <Stars value={value} />
                      </div>
                    );
                  })}
                </div>
                {row.comment ? <p className="review-comment">“{row.comment}”</p> : null}
                <Link to={`/bookings/${row.booking_id}`} className="review-link">
                  Open visit
                </Link>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}
